import React, { useState, useEffect, useCallback} from 'react';
import { useOutletContext } from 'react-router-dom';
import { db, functions} from '../../../firebase';
import { collection, query, getDocs, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import TabelaCRM from '../components/TabelaCRM';
import FormularioDinamico from '../components/FormularioDinamico';

const Empresas = () => {
    const { workspaceData } = useOutletContext();
    const [view, setView] = useState('LIST'); // 'LIST' ou 'FORM'
    const [empresas, setEmpresas] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [editingEmpresa, setEditingEmpresa] = useState(null);

    // --- FUNÇÕES DE CARREGAMENTO ---
    const carregarEmpresas = useCallback(async () => {
        if (!workspaceData?.cnpj_completo) return;
        const q = query(collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_empresas'));
        const snapshot = await getDocs(q);
        const lista = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setEmpresas(lista);
    }, [workspaceData?.cnpj_completo]);

    useEffect(() => {
        carregarEmpresas();
    }, [carregarEmpresas]);

    // --- LÓGICA DE BUSCA NA BRASILAPI (VIA PROXY BACKEND - CORS FIX) ---
    const buscarDadosCNPJ = async (cnpjLimpo) => {
        setIsProcessing(true);
        try {
            const consultarCnpj = httpsCallable(functions, 'consultarCnpjBrasilApi');
            const result = await consultarCnpj({ cnpj: cnpjLimpo });
            const data = result.data;

            // Mapeamento Cirúrgico: Ajustado para dar "Match" exato com os names do camposForm
            return {
                cnpj: data.cnpj,
                razao_social: data.razao_social,
                nome_fantasia: data.nome_fantasia || data.razao_social,
                logradouro: data.logradouro,
                numero: data.numero,
                complemento: data.complemento || "",
                bairro: data.bairro,
                municipio: data.municipio,
                uf: data.uf,
                cep: data.cep,
                contato_telefone: data.ddd_telefone_1 || "", // Ajustado para preencher o WhatsApp
                contato_email: data.email || "",             // Ajustado para preencher o e-mail
                status: "ATIVO"
            };
        } catch (error) {
            alert(error.message);
            return null;
        } finally {
            setIsProcessing(false);
        }
    };

    // --- GATILHO SILENCIOSO: Ouve a digitação e dispara a busca no 14º dígito ---
    const handleFieldChange = async (name, value) => {
        if (name === 'cnpj') {
            const cnpjLimpo = value.replace(/\D/g, '');
            if (cnpjLimpo.length === 14) {
                const dados = await buscarDadosCNPJ(cnpjLimpo);
                if (dados) {
                    // Injeta os dados preservando o CNPJ formatado/digitado
                    setEditingEmpresa(prev => ({ ...prev, ...dados, cnpj: value }));
                }
            }
        }
    };

    // --- DEFINIÇÃO DE COLUNAS (TabelaCRM) ---
    const colunas = [
        { header: 'Empresa', accessor: 'nome_fantasia' },
        { header: 'CNPJ', accessor: 'cnpj' },
        { header: 'Cidade/UF', render: (row) => `${row.municipio}/${row.uf}` },
        { header: 'Tipo', accessor: 'tipo' },
        { header: 'Status', render: (row) => (
            <span style={{ color: row.status === 'ATIVO' ? '#10b981' : '#ef4444', fontWeight: 'bold' }}>{row.status}</span>
        )}
    ];

    // --- DEFINIÇÃO DE CAMPOS (FormularioDinamico) ---
    const camposForm = [
        { name: 'cnpj', label: 'CNPJ da Empresa (Busca Automática)', type: 'text', required: true, colSpan: 12, placeholder: 'Digite os 14 números...' },
        { name: 'razao_social', label: 'Razão Social (Imutável)', type: 'text', disabled: true, colSpan: 12 },
        { name: 'nome_fantasia', label: 'Nome Fantasia', type: 'text', disabled: true, colSpan: 6 },
        { name: 'tipo', label: 'Tipo de Cliente', type: 'select', required: true, colSpan: 6, options: [
            { label: 'Construtora', value: 'CONSTRUTORA' },
            { label: 'Concessionária', value: 'CONCESSIONARIA' },
            { label: 'Órgão Público', value: 'ORGAO_PUBLICO' },
            { label: 'Laboratório Parceiro', value: 'LABORATORIO_PARCEIRO' }
        ]},
        { name: 'cep', label: 'CEP', type: 'text', disabled: true, colSpan: 4 },
        { name: 'municipio', label: 'Cidade', type: 'text', disabled: true, colSpan: 6 },
        { name: 'uf', label: 'UF', type: 'text', disabled: true, colSpan: 2 },
        { name: 'logradouro', label: 'Endereço', type: 'text', disabled: true, colSpan: 9 },
        { name: 'numero', label: 'Nº', type: 'text', disabled: true, colSpan: 3 },
        { name: 'complemento', label: 'Complemento', type: 'text', disabled: true, colSpan: 12 },
        
        { name: 'divider', label: 'DADOS DO CONTATO PRINCIPAL', type: 'title', colSpan: 12 },
        { name: 'contato_nome', label: 'Responsável (Decisor)', type: 'text', required: true, colSpan: 6 },
        { name: 'contato_email', label: 'E-mail para Laudos', type: 'email', required: true, colSpan: 6 },
        { name: 'contato_telefone', label: 'WhatsApp do Contato', type: 'text', required: true, colSpan: 6 }
    ];

    const handleSaveEmpresa = async (formData) => {
        setIsProcessing(true);
        try {
            const empresaRef = doc(db, 'workspaces', workspaceData.cnpj_completo, 'crm_empresas', formData.cnpj);
            await setDoc(empresaRef, {
                ...formData,
                updated_at: serverTimestamp(),
                created_at: editingEmpresa?.created_at || serverTimestamp()
            }, { merge: true });

            alert("✅ Empresa salva e sincronizada com sucesso!");
            setView('LIST');
            carregarEmpresas();
        } catch (e) {
            alert("Erro ao salvar: " + e.message);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div style={{ padding: '30px', height: '100%', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h2 style={{ color: '#0f172a', margin: 0 }}>Gestão de Clientes (B2B)</h2>
                    <p style={{ color: '#64748b', margin: 0 }}>Base de dados sincronizada com a Receita Federal via BrasilAPI.</p>
                </div>
                <button 
                    className="pts-btn-primary" 
                    onClick={() => {
                        setEditingEmpresa(null);
                        setView(view === 'LIST' ? 'FORM' : 'LIST');
                    }}
                >
                    {view === 'LIST' ? '+ Cadastrar Empresa' : '← Voltar para Lista'}
                </button>
            </div>

            {view === 'LIST' ? (
                <TabelaCRM 
                    columns={colunas} 
                    data={empresas} 
                    onRowClick={(row) => {
                        setEditingEmpresa(row);
                        setView('FORM');
                    }}
                />
            ) : (
                <div style={{ maxWidth: '800px', margin: '0 auto', background: '#fff', padding: '30px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                    <FormularioDinamico 
                        fields={camposForm} 
                        initialData={editingEmpresa}
                        onSubmit={handleSaveEmpresa}
                        onCancel={() => setView('LIST')}
                        isProcessing={isProcessing}
                        submitLabel={editingEmpresa ? "Atualizar Cadastro" : "Finalizar Cadastro"}
                        onFieldChange={handleFieldChange} // <-- O CABO DE IGNIÇÃO CONECTADO
                    />
                </div>
            )}
        </div>
    );
};

export default Empresas;